import React, { useEffect, useMemo, useState } from "react";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import StyledInput from "components/styledInput/StyledInput";
import Banner from "components/banner/banner";
import Button from "components/button";
import PerimeterAccessAlert from "components/modal/dataModal/data/perimeter/PerimeterAccessAlert";
import PerimeterModalFooter from "components/modal/dataModal/data/perimeter/PerimeterModalFooter";
import usePerimeterModal from "components/modal/dataModal/data/perimeter/usePerimeterModal";
import { NO_PERIMETER_TARGET } from "pages/profil/perimeters/perimeterUsage";
import { ARCHIVE_ERROR_OVERRIDES } from "pages/profil/perimeters/perimeterErrors";
import { fillText, getCountText } from "pages/profil/perimeters/perimeterText";

// Active missions or members are attached: they move to another perimeter, or
// to none, before the perimeter is archived.
const TransferArchivePerimeterModal = () => {
    const { language, perimeterModalData } = UseContext();
    const { isSaving, setIsSaving, close, succeed, fail } = usePerimeterModal();
    const [{ perimeter, usage, perimeters }] = useState(
        () => perimeterModalData || {}
    );
    const perimeterId = perimeter?.id;
    const noPerimeterLabel = language.getValue(
        "perimeters.archive.noPerimeter"
    );

    const options = useMemo(
        () => [
            ...(perimeters || [])
                .filter((other) => other.id !== perimeterId)
                .map((other) => ({ value: other.id, label: other.name })),
            { value: NO_PERIMETER_TARGET, label: noPerimeterLabel },
        ],
        [perimeters, perimeterId, noPerimeterLabel]
    );
    const [target, setTarget] = useState(options[0].value);
    // Members left without perimeter, per destination. The usage opened with
    // the "none" destination; the others are asked when first picked.
    const [leftByTarget, setLeftByTarget] = useState({
        [NO_PERIMETER_TARGET]: Number(usage?.membersLeftWithoutPerimeter) || 0,
    });

    // Archiving stays disabled until the answer for the chosen destination is in.
    const [hasUsageFailed, setHasUsageFailed] = useState(false);
    const [usageAttempt, setUsageAttempt] = useState(0);

    useEffect(() => {
        setHasUsageFailed(false);
        if (target in leftByTarget) return undefined;
        let cancelled = false;
        ApiDatabase.getPerimeterUsage(
            { id: perimeterId, transferTo: target },
            (res) => {
                if (cancelled) return;
                setLeftByTarget((known) => ({
                    ...known,
                    [target]:
                        Number(res?.data?.membersLeftWithoutPerimeter) || 0,
                }));
            },
            (err) => {
                if (cancelled) return;
                console.error(err);
                setHasUsageFailed(true);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [target, leftByTarget, perimeterId, usageAttempt]);

    const isUsageKnown = target in leftByTarget;
    const membersLeft = leftByTarget[target] || 0;
    // The archived perimeter's own name for the "none" toast, else the target's.
    const targetName =
        target === NO_PERIMETER_TARGET
            ? perimeter?.name
            : options.find((option) => option.value === target)?.label;

    const handleConfirm = () => {
        if (isSaving || !isUsageKnown) return;
        setIsSaving(true);
        ApiDatabase.postArchivePerimeter(
            {
                id: perimeterId,
                transferTo: target === NO_PERIMETER_TARGET ? null : target,
            },
            (res) => {
                const transferred = Number(res?.data?.transferredMissions) || 0;
                succeed(
                    transferred > 0
                        ? getCountText(
                              language,
                              target === NO_PERIMETER_TARGET
                                  ? "perimeters.toast.archivedNoPerimeter"
                                  : "perimeters.toast.transferred",
                              transferred,
                              { name: targetName }
                          )
                        : language.getValue("perimeters.toast.archived")
                );
            },
            (err) => fail(err, { overrides: ARCHIVE_ERROR_OVERRIDES })
        );
    };

    return (
        <>
            <Modalheader
                hide={close}
                hideShow={true}
                description={fillText(
                            language.getValue(
                                "perimeters.archive.transferIntro"
                            ),
                            {
                                missions: getCountText(
                                    language,
                                    "perimeters.archive.activeMissions",
                                    Number(usage?.activeMissions) || 0
                                ),
                                members: getCountText(
                                    language,
                                    "perimeters.list.members",
                                    Number(usage?.members) || 0
                                ),
                            }
                        )}
            >
                {fillText(
                    language.getValue("perimeters.archive.transferTitle"),
                    {
                        name: perimeter?.name,
                    }
                )}
            </Modalheader>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <StyledInput
                        type="select"
                        label={language.getValue(
                            "perimeters.archive.transferLabel"
                        )}
                        list={options}
                        value={target}
                        onChange={(event) => setTarget(event.target.value)}
                        isDisabled={isSaving}
                    />
                    {membersLeft > 0 && (
                        <PerimeterAccessAlert count={membersLeft} />
                    )}
                    {hasUsageFailed && (
                        <div className="flex flex-col items-start gap-3">
                            <Banner type="alert" showIcon={false}>
                                {language.getValue(
                                    "perimeters.archive.usageError"
                                )}
                            </Banner>
                            <Button
                                color="SECONDARY"
                                size="SM"
                                onClick={() =>
                                    setUsageAttempt((count) => count + 1)
                                }
                            >
                                {language.getValue("perimeters.list.retry")}
                            </Button>
                        </div>
                    )}
                </div>
            </ModalBody>
            <PerimeterModalFooter
                confirmLabel={language.getValue("perimeters.archive.confirm")}
                savingLabel={language.getValue("perimeters.archive.archiving")}
                isSaving={isSaving}
                canConfirm={isUsageKnown}
                onCancel={close}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default TransferArchivePerimeterModal;
